import { ReportTemplateIdEnum } from "../../enums";

export const REPORT_CELL_COLOR_CLASS = {
  Green: "cell_status_green",
  Red: "cell_status_red",
  None: "",
} as const;

export type ReportCellColorClass =
  (typeof REPORT_CELL_COLOR_CLASS)[keyof typeof REPORT_CELL_COLOR_CLASS];

export type CellColorRule =
  | {
      type: "numberRange";
      minInclusive: number;
      maxInclusive: number;
      className: ReportCellColorClass;
    }
  | {
      type: "stringEquals";
      equals: string;
      caseInsensitive?: boolean;
      className: ReportCellColorClass;
    }
  | {
      type: "fallback";
      className: ReportCellColorClass;
    };

export type CellColoringConfig = {
  ignoreValues?: readonly string[];
  parseNumber?: "never" | "ifString";
  rules: readonly CellColorRule[];
};

const DEFAULT_IGNORE_VALUES = ["--", ""] as const;

const tryParseNumber = ({ value }: { value: unknown }): number | undefined => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) {
    return undefined;
  }

  return parsed;
};

export const resolveCellColorClass = ({
  value,
  config,
}: {
  value: unknown;
  config: CellColoringConfig | undefined;
}): ReportCellColorClass => {
  if (!config) {
    return REPORT_CELL_COLOR_CLASS.None;
  }

  const valueAsString =
    value === null || value === undefined ? "" : String(value);
  const ignoreValues = config.ignoreValues ?? DEFAULT_IGNORE_VALUES;

  if (ignoreValues.includes(valueAsString)) {
    return REPORT_CELL_COLOR_CLASS.None;
  }

  const numberValue =
    config.parseNumber === "ifString"
      ? tryParseNumber({ value: valueAsString })
      : undefined;

  const matchedRule = config.rules.find(rule => {
    if (rule.type === "numberRange") {
      if (numberValue === undefined) {
        return false;
      }

      return (
        numberValue >= rule.minInclusive && numberValue <= rule.maxInclusive
      );
    }

    if (rule.type === "stringEquals") {
      const left = rule.caseInsensitive
        ? valueAsString.toLowerCase()
        : valueAsString;
      const right = rule.caseInsensitive
        ? rule.equals.toLowerCase()
        : rule.equals;
      return left === right;
    }

    return rule.type === "fallback";
  });

  return matchedRule?.className ?? REPORT_CELL_COLOR_CLASS.None;
};

const TIME_DEVIATION_MIN_MATCH = -1;
const TIME_DEVIATION_MAX_MATCH = 1;

const REPORT_CELL_COLORING_BY_TEMPLATE: Partial<
  Record<ReportTemplateIdEnum, Partial<Record<string, CellColoringConfig>>>
> = {
  [ReportTemplateIdEnum.PLAN_VS_EXECUTION_REPORT]: {
    timeDeviationMinutes: {
      ignoreValues: DEFAULT_IGNORE_VALUES,
      parseNumber: "ifString",
      rules: [
        {
          type: "numberRange",
          minInclusive: TIME_DEVIATION_MIN_MATCH,
          maxInclusive: TIME_DEVIATION_MAX_MATCH,
          className: REPORT_CELL_COLOR_CLASS.Green,
        },
        { type: "fallback", className: REPORT_CELL_COLOR_CLASS.Red },
      ],
    },
    locationDeviation: {
      ignoreValues: DEFAULT_IGNORE_VALUES,
      rules: [
        {
          type: "stringEquals",
          equals: "Match",
          caseInsensitive: true,
          className: REPORT_CELL_COLOR_CLASS.Green,
        },
        { type: "fallback", className: REPORT_CELL_COLOR_CLASS.Red },
      ],
    },
  },
};

export const resolveReportCellColorClass = ({
  reportTemplateId,
  uiTableColumn,
  value,
}: {
  reportTemplateId: ReportTemplateIdEnum | undefined;
  uiTableColumn: string;
  value: unknown;
}): ReportCellColorClass => {
  if (!reportTemplateId) {
    return REPORT_CELL_COLOR_CLASS.None;
  }

  const config =
    REPORT_CELL_COLORING_BY_TEMPLATE[reportTemplateId]?.[uiTableColumn];
  return resolveCellColorClass({ value, config });
};
